import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useViews } from "@/hooks/useViews";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { captureError } from '@/lib/sentry';
import { SortableFilterRow } from "@/components/contacts/SortableFilterRow";
import type { SavedView } from "@/hooks/useViews";

export function FiltersPage() {
  const { views, loading, deleteView, reorderViews } = useViews();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentWorkspace, loading: workspaceLoading } = useWorkspaces();

  // Local ordered copy so drag reordering is optimistic.
  const [orderedViews, setOrderedViews] = useState<SavedView[]>(views);

  useEffect(() => {
    setOrderedViews(views);
  }, [views]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDeleteView = async (id: string) => {
    try {
      await deleteView(id);
      toast({
        description: "Filter deleted successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'filters-list-delete-view' });
      toast({
        title: "Error",
        description: "Failed to delete filter",
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Compute the new order outside the state updater. Doing the persistence
    // (and arrayMove) inside a setState updater double-fires the RPC under
    // dev StrictMode, since React double-invokes updater functions.
    const oldIndex = orderedViews.findIndex((v) => v.id === active.id);
    const newIndex = orderedViews.findIndex((v) => v.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(orderedViews, oldIndex, newIndex);
    setOrderedViews(reordered);
    // Persist via the batched RPC; the hook resyncs on error.
    void reorderViews(reordered.map((v) => v.id)).catch(() => {
      /* hook already surfaced toast + refetched */
    });
  };

  if (workspaceLoading) {
    return (
      <div className="max-w-[1400px] mx-auto w-full py-8 px-4">
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="max-w-[1400px] mx-auto w-full py-8 px-4">
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center">
            <p className="text-muted-foreground">No workspace selected</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto w-full py-8 px-4">
      <h1 className="text-2xl font-semibold tracking-tight">All Filters</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">
        Filters and their order are shared with everyone in your workspace.
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-[200px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading filters...</p>
          </div>
        </div>
      ) : orderedViews.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedViews.map((v) => v.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {orderedViews.map((view) => (
                <SortableFilterRow
                  key={view.id}
                  view={view}
                  onOpen={(id) => navigate(`/filters/${id}`)}
                  onDelete={handleDeleteView}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No saved filters yet. Create a filter from the Contacts page to get started.
        </div>
      )}
    </div>
  );
}
